import { useMutation, useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Footer, TitleBar } from '../../../../../components';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import {
  DocumentLinks,
  OwnersInformation,
  SummaryContent,
} from '../../components';

import {
  CREATE_HARDWARE,
  CREATE_SOFTWARE,
  DELETE_HARDWARE,
  DELETE_SOFTWARE,
  FIND_ALL_HARDWARES_BY_IT_RESOURCE,
  FIND_ALL_SOFTWARES_BY_IT_RESOURCE,
  FIND_IT_RESOURCE_BY_STRATEGY,
  GET_SECTION_OWNER,
  UPDATE_HARDWARE,
  UPDATE_IT_RESOURCE,
  UPDATE_SECTION_OWNER,
  UPDATE_SOFTWARE,
} from '../../../queries';
import { GET_DOCUMENT_LINKS_BY_IT_RESOURCES } from '../../../queries/documentLink';
import { STRATEGY_MODELS, STRATEGY_TYPES } from '../constants';
import { formFieldsSoftware } from './formFieldsSoftware';
import { formFieldsHardware } from './formFieldsHardware';
import { tableSortingMethods } from '../../../../../utils/tableSortingMethods';
import { cleanUpObject } from '../../../../../utils';
import { ExportColumn } from '../../../../../interfaces/ExportColumn';
import { baseColumnsHardware, baseColumnsITApps } from './exportColumns';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

type StrategyFacilityProps = {
  strategyType?: string;
};

const ItResources = ({
  strategyType = 'processArea',
}: StrategyFacilityProps) => {
  const { locationId, processAreaId, strategyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const locationName = searchParams.get('locationName');
  const strategyLength = searchParams.get('strategyLength');

  let strategyName;

  if (strategyType === 'processArea') {
    strategyName = searchParams.get('name');
  }

  const subtitle =
    strategyType === 'processArea'
      ? `${locationName} // ${strategyName} // ${strategyLength} Month Strategy`
      : `${locationName} // ${strategyLength} Month Strategy`;

  const backPath =
    strategyType === 'processArea'
      ? `/process-areas-and-strategies/${locationId}/${processAreaId}`
      : `/strategy-summary/${locationId}/${strategyId}?locationName=${locationName}&strategyLength=${strategyLength}`;

  const OPERATION_NAMES = {
    SECTION_OWNER: 'GetSectionOwnerByStrategy',
    SOFTWARE: 'FindAllSoftwaresByItResource',
    HARDWARE: 'FindAllHardwaresByItResource',
    STRATEGY_IT: 'FindITResourceByStrategy',
    DOCUMENTS: 'FindAllByITResource',
  };

  const {
    data: strategyITResource,
    loading: strategyITResourceLoading,
    error: strategyITResourceError,
  } = useQuery(FIND_IT_RESOURCE_BY_STRATEGY, {
    variables: {
      uuid: strategyId,
    },
    skip: !strategyId,
  });

  const strategyITResourceData = strategyITResource?.findITResourceByStrategy;

  const { data: strategyOwner, error: strategyOwnerError } = useQuery(
    GET_SECTION_OWNER,
    {
      variables: {
        uuid: strategyId,
        strategyType: STRATEGY_TYPES.IT_RESOURCES,
      },
      skip: !strategyId,
    },
  );

  const sectionOwner = strategyOwner?.findSectionOwnerByStrategy;

  const {
    data: software,
    loading: softwareLoading,
    error: softwareError,
  } = useQuery(FIND_ALL_SOFTWARES_BY_IT_RESOURCE, {
    variables: {
      uuid: strategyITResourceData?.uuid,
    },
    skip: !strategyITResourceData?.uuid,
  });

  const softwareData = software?.findAllSoftwaresByItResource;

  const {
    data: hardware,
    loading: hardwareLoading,
    error: hardwareError,
  } = useQuery(FIND_ALL_HARDWARES_BY_IT_RESOURCE, {
    variables: {
      uuid: strategyITResourceData?.uuid,
    },
    skip: !strategyITResourceData?.uuid,
  });

  const hardwareData = hardware?.findAllHardwaresByItResource;

  const { data: documents, error: documentsError } = useQuery(
    GET_DOCUMENT_LINKS_BY_IT_RESOURCES,
    {
      variables: {
        uuid: strategyITResourceData?.uuid,
      },
      skip: !strategyITResourceData?.uuid,
    },
  );

  const documentsData = documents?.findAllByITResource;

  const [updateSectionOwner] = useMutation(UPDATE_SECTION_OWNER, {
    refetchQueries: [OPERATION_NAMES.SECTION_OWNER],
  });

  const [createSoftware] = useMutation(CREATE_SOFTWARE, {
    refetchQueries: [OPERATION_NAMES.SOFTWARE],
  });

  const [updateSoftware] = useMutation(UPDATE_SOFTWARE, {
    refetchQueries: [OPERATION_NAMES.SOFTWARE],
  });

  const [deleteSoftware] = useMutation(DELETE_SOFTWARE, {
    refetchQueries: [OPERATION_NAMES.SOFTWARE],
  });

  const [createHardware] = useMutation(CREATE_HARDWARE, {
    refetchQueries: [OPERATION_NAMES.HARDWARE],
  });

  const [updateHardware] = useMutation(UPDATE_HARDWARE, {
    refetchQueries: [OPERATION_NAMES.HARDWARE],
  });

  const [deleteHardware] = useMutation(DELETE_HARDWARE, {
    refetchQueries: [OPERATION_NAMES.HARDWARE],
  });

  const [updateStrategyITResource] = useMutation(UPDATE_IT_RESOURCE, {
    refetchQueries: [OPERATION_NAMES.STRATEGY_IT],
  });

  const applicationsColumns: ColumnsType<any> = [
    {
      key: 'name',
      title: 'IT Application',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: ({ name }) => (
        <span className="custom-table--column custom-table--column-primary">
          {name}
        </span>
      ),
    },
    {
      key: 'impactedByLocalizedEvent',
      title: 'Impacted by Localized Event?',
      width: 100,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(
          a.impactedByLocalizedEvent,
          b.impactedByLocalizedEvent,
        ),
      render: ({ impactedByLocalizedEvent }) =>
        impactedByLocalizedEvent !== undefined &&
        impactedByLocalizedEvent !== null ? (
          <span className="custom-table--column">
            {impactedByLocalizedEvent ? 'Yes' : 'No'}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'redundancyAvailable',
      title: 'Redundancy Available?',
      width: 100,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(
          a.redundancyAvailable,
          b.redundancyAvailable,
        ),
      render: ({ redundancyAvailable }) =>
        redundancyAvailable !== undefined && redundancyAvailable !== null ? (
          <span className="custom-table--column">
            {redundancyAvailable ? 'Yes' : 'No'}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'rampUpTime',
      title: () => (
        <div>
          Ramp-Up Time/Failover Time
          <br />
          (hours)
        </div>
      ),
      width: 100,
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.rampUpTime, b.rampUpTime),
      render: ({ rampUpTime }) =>
        rampUpTime ? (
          <span className="custom-table--column">{rampUpTime}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },

    {
      key: 'internalContact',
      title: 'Internal Contact',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.contactFirstName, b.contactFirstName),
      render: ({ contactFirstName, contactLastName, email }) =>
        contactFirstName || contactLastName || email ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>
              {contactFirstName} {contactLastName}
            </span>
            <span>{email}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const hardwareColumns: ColumnsType<any> = [
    {
      key: 'name',
      title: 'Hardware',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: ({ name }) => (
        <span className="custom-table--column custom-table--column-primary">
          {name}
        </span>
      ),
    },
    {
      key: 'countNeeded',
      title: 'Count Needed',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.countNeeded, b.countNeeded),
      render: ({ countNeeded }) =>
        countNeeded >= 0 ? (
          <span className="custom-table--column">{countNeeded}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'leadTime',
      title: 'Lead Time (months)',
      width: 100,
      align: 'right',
      sorter: (a, b) => tableSortingMethods.number(a.leadTime, b.leadTime),
      render: ({ leadTime }) =>
        leadTime ? (
          <span className="custom-table--column">{leadTime}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'costPerUnit',
      title: 'Cost Per Unit',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.costPerUnit, b.costPerUnit),
      render: ({ costPerUnit }) =>
        costPerUnit >= 0 ? (
          <span className="custom-table--column">${costPerUnit}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'totalCost',
      title: 'Total Cost',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.countNeeded * a.costPerUnit,
          b.countNeeded * b.costPerUnit,
        ),
      render: ({ countNeeded, costPerUnit }) =>
        countNeeded >= 0 && costPerUnit >= 0 ? (
          <span className="custom-table--column">
            {`$${countNeeded * costPerUnit}`}
          </span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'internalContact',
      title: 'Internal Contact',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.contactFirstName, b.contactFirstName),
      render: ({ contactFirstName, contactLastName, email }) =>
        contactFirstName || contactLastName || email ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>
              {contactFirstName} {contactLastName}
            </span>
            <span>{email}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const modifyVariablesSoftware = ({ values, type }: ModifyVariablesArgs) => {
    const {
      impactedByLocalizedEvent,
      redundancyAvailable,
      sectionOwnerfirstName,
      sectionOwnerlastName,
      rampUpTime,
      ...restValues
    } = values;

    const commonValues = {
      ...restValues,
      contactFirstName: sectionOwnerfirstName,
      contactLastName: sectionOwnerlastName,
      impactedByLocalizedEvent:
        impactedByLocalizedEvent && impactedByLocalizedEvent === 'true',
      redundancyAvailable:
        redundancyAvailable && redundancyAvailable === 'true',
      rampUpTime: String(rampUpTime),
    };

    const cleanedValues = cleanUpObject(commonValues);

    if (type === 'create') {
      return {
        ...cleanedValues,
        itResource: {
          uuid: strategyITResourceData?.uuid,
        },
      };
    }

    return cleanedValues;
  };

  const modifyVariablesHardware = ({ values, type }: ModifyVariablesArgs) => {
    const {
      sectionOwnerfirstName,
      sectionOwnerlastName,
      leadTime,
      ...restValues
    } = values;

    const commonValues = {
      ...restValues,
      contactFirstName: sectionOwnerfirstName,
      contactLastName: sectionOwnerlastName,
      countNeeded: Number(values.countNeeded),
      costPerUnit: Number(values.costPerUnit),
      leadTime: String(leadTime),
    };

    const cleanedValues = cleanUpObject(commonValues);

    if (type === 'create') {
      return {
        ...cleanedValues,
        itResource: {
          uuid: strategyITResourceData?.uuid,
        },
      };
    }

    return cleanedValues;
  };

  return (
    <>
      <TitleUpdater title={`IT Resources`} />
      <TitleBar title="BCP Hub" subtitle={subtitle} />
      <section className="flex flex-col gap-4">
        <OwnersInformation
          title="IT Resources Section Owner"
          data={sectionOwner}
          updateGQL={updateSectionOwner}
          error={strategyOwnerError}
        />
        <QueryErrorBoundary error={softwareError}>
          <TableV3
            title="IT Applications"
            columns={applicationsColumns}
            data={softwareData}
            loading={softwareLoading}
            editable
            formFields={formFieldsSoftware}
            createGQL={createSoftware}
            updateGQL={updateSoftware}
            deleteGQL={deleteSoftware}
            modifyVariables={modifyVariablesSoftware}
            exportExcelColumns={baseColumnsITApps}
          />
        </QueryErrorBoundary>
        <QueryErrorBoundary error={hardwareError}>
          <TableV3
            title="IT Hardware"
            columns={hardwareColumns}
            data={hardwareData}
            loading={hardwareLoading}
            editable
            formFields={formFieldsHardware}
            createGQL={createHardware}
            updateGQL={updateHardware}
            deleteGQL={deleteHardware}
            modifyVariables={modifyVariablesHardware}
            exportExcelColumns={baseColumnsHardware}
          />
        </QueryErrorBoundary>
        <section className="w-full grid xl:grid-cols-2 gap-4">
          <SummaryContent
            title="IT Resources Commentary"
            data={strategyITResourceData}
            loading={strategyITResourceLoading}
            error={strategyITResourceError}
            updateGQL={updateStrategyITResource}
          />
          <DocumentLinks
            data={documentsData}
            error={documentsError}
            parentUuid={strategyITResourceData?.uuid}
            queryToUpdate={OPERATION_NAMES.DOCUMENTS}
            model={STRATEGY_MODELS.IT_RESOURCES}
          />
        </section>
        <Footer backPath={backPath} />
      </section>
    </>
  );
};

export default ItResources;
